import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Text } from '@mantine/core';

/**
 * A number shown as text that turns into a field when clicked.
 *
 * Same behaviour as the date cell next to it - commit on Enter or blur, discard
 * on Escape - because a table where one cell commits on Enter and its neighbour
 * does not is a table people stop trusting.
 */
export function NumberCell({
  value,
  onCommit,
  min = 0,
  suffix,
  title,
  negative,
}: {
  value: number;
  onCommit: (next: number) => void;
  min?: number;
  suffix?: string;
  title?: string;
  /** Renders in the critical colour, for a value that means trouble. */
  negative?: boolean;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);
  // After editing, focus returns to the cell, so keyboard users continue
  // with Tab from where they were instead of from the top of the page.
  const displayRef = useRef<HTMLParagraphElement>(null);
  const refocus = useRef(false);
  useEffect(() => {
    if (!editing && refocus.current) {
      refocus.current = false;
      displayRef.current?.focus();
    }
  }, [editing]);

  useEffect(() => setDraft(String(value)), [value]);
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = (): void => {
    setEditing(false);
    const parsed = Number(draft.replace(',', '.'));
    if (Number.isInteger(parsed) && parsed >= min && parsed !== value) {
      onCommit(parsed);
    } else {
      setDraft(String(value));
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        inputMode="numeric"
        value={draft}
        onChange={(event) => setDraft(event.currentTarget.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            refocus.current = true;
            commit();
          }
          if (event.key === 'Escape') {
            refocus.current = true;
            setDraft(String(value));
            setEditing(false);
          }
        }}
        style={{
          width: '100%',
          textAlign: 'right',
          border: '1px solid var(--mantine-color-brand-5)',
          borderRadius: 'var(--mantine-radius-sm)',
          padding: '1px 4px',
          font: 'inherit',
          background: 'var(--mantine-color-body)',
          color: 'var(--mantine-color-text)',
        }}
      />
    );
  }

  return (
    <Text
      ref={displayRef}
      size="sm"
      ta="right"
      c={negative ? 'critical.6' : undefined}
      title={title ?? 'Zum Ändern anklicken'}
      className="pst-cell pst-num"
      role="button"
      tabIndex={0}
      onClick={() => setEditing(true)}
      onKeyDown={(event) => {
        // Enter or F2 opens the cell, as in a spreadsheet.
        if (event.key === 'Enter' || event.key === 'F2') {
          event.preventDefault();
          setEditing(true);
        }
      }}
      style={{ whiteSpace: 'nowrap' }}
    >
      {value}
      {suffix ? <Text component="span" size="xs" c="dimmed"> {suffix}</Text> : null}
    </Text>
  );
}
